import { Button, Card, Flex, ProviderConsumer } from '@fluentui/react-northstar';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { appState, useSetState } from '../../AppState';
import { ScopeErrorMessage } from '../../Rbac/ScopePermissionNoAccess';
import { getCachedFeature } from '../../shared/cache/FeatureCache';
import { startFileUploadSession } from '../../shared/components/EditTicketAttachments/NewAttachmentFlow/NewAttachmentFlowContent';
import { InitialPageLoader } from '../../shared/components/InitialPageLoader/InitialPageLoader';
import { TicketCustomFormSection } from '../../shared/components/TicketCustomFormTab/TicketCustomFormSection';
import { graphService } from '../../shared/services/graph.service';
import { platformService } from '../../shared/services/platform.service';
import { uploadService } from '../../shared/services/upload.service';
import { FILTERS_STRINGS, LICENSE_FEATURE, TicketApprovalState } from '../../shared/utils/constants';
import { parseIfJSON } from '../../shared/utils/helper';
import { UploadFile } from '../../templates/CustomComponents/UploadFile';
import { CustomForm, CustomFormAttachmentsProps } from '../../templates/CustomForms/CustomForm.Interface';
import { markFormsAsUnanswerd, parseCardTemplate, setCustomFormTemplateData, updateCustomFormListWithAnswers, validateRequiredInputs } from '../../templates/CustomForms/CustomForm.Service';
import { CreateRequest_ActionSet, CreateRequest_TriggerExecuteButton, CreateRequestAdaptiveCard, FormatCardAnswerJson, RemoveRequest_ActionSet } from './CreateRequest';
import { ReloadEndUserCFOptions } from './Requests';

const EmailRequest: React.FC = () => {
  const { search } = useLocation();
  const { t } = useTranslation();
  const uploadHelper = new uploadService();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const api = new platformService();
  const graphAPI = new graphService();
  const currentState = appState();
  const setAppState = useSetState();
  const [state, setState] = useState(0);
  const [answer, setAnswer] = useState("");
  const [approvalTitle, setApprovalTitle] = useState("");
  const [templateId, setTemplateId] = useState(0);
  const [templateResponse, setTemplateResponse] = useState({});
  const [emailConfig, setEmailConfig] = useState<any>({});
  const [defaultTeam, setDefaultTeam] = useState();
  const [request, setRequest] = useState("");
  const [defaultTemplateId, setDefaultTemplateId] = useState<number | null>(null);
  const [isRenderComplete, setIsRenderComplete] = useState(false);
  const [isSubmitClicked, setIsSubmitClicked] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [dataSourceList, setDataSourceList] = useState([]);
  const [currentTemplate, setCurrentTemplate] = useState<any>({});
  const [customForms, setCustomForms] = useState([]);
  const [isSummaryPage, setIsSummaryPage] = useState(false);
  const [showSummaryCard, setShowSummaryCard] = useState(false);
  const [continueButtonText, setContinueButtonText] = useState("");
  const [activeForm, setActiveForm] = useState<CustomForm>(null);
  const [isResetForm, setIsResetForm] = useState(false);
  const [goToNextForm, setGoToNextForm] = useState(false);
  const [customFormListAttachmentsProps, setCustomFormListAttachmentsProps] = useState<CustomFormAttachmentsProps[]>([]);
  const [isCustomFormLoading, setIsCustomFormLoading] = useState(false);
  
  const decodeBase64Unicode = (data: any) => {
    const binaryStr = atob(data);
    
    const bytes = new Uint8Array(binaryStr.length);
    for (let i = 0; i < binaryStr.length; i++) {
        bytes[i] = binaryStr.charCodeAt(i);
    }
    
    return new TextDecoder('utf-8').decode(bytes);
  }

  const newrequest = async (data: any) => {
    try {
      const results = await Promise.all([
        api.getTeamsSafe(FILTERS_STRINGS.IS_DEFAULT),
        api.getEmailConnectorConfiguration(),
        api.getTeamsSafe(`?$filter=Id eq ${data.teamId ?? 0}`),
        api.getTicketTemplates(`?$filter=IsDefault%20eq%20true&v=${new Date().getTime()}&$expand=TemplateForms($expand=Form)`),
        api.getDataSources(`${FILTERS_STRINGS.FILTERS_URL_PARAMS}`)
      ]);
      const tc = results[1].data || {};
      setDefaultTeam(data.teamId ?? results[0][0]?.Id ?? null);
      const thisEnabledMultiDepartment = await getCachedFeature(LICENSE_FEATURE.MultiDepartment);
      if (thisEnabledMultiDepartment)
        tc.EnableMultiDepartmentPicker = results[2][0]?.EnableMultiDepartmentPicker ?? tc.EnableMultiDepartmentPicker;

      setEmailConfig(tc);
      setRequest(data.responseSubject);
      setDataSourceList(results[4].data.value ?? []);
      if (tc.EnableMultiDepartmentPicker && thisEnabledMultiDepartment) {
        const defaultTemplate = results[3].data.value[0];
        if (defaultTemplate?.CardResponseJson) {
          const cardPayload = JSON.parse(defaultTemplate.CardResponseJson);
          cardPayload.body.push(CreateRequest_ActionSet(t));

          if (cardPayload)
            setAnswer(JSON.stringify(cardPayload));

          if(defaultTemplate?.TemplateForms.length > 0){
            const templateForm = defaultTemplate.TemplateForms[0];
            const cardPayload = JSON.parse(templateForm.Form.Definition);
          
            if(cardPayload)
              setAnswer(JSON.stringify(cardPayload));

            setCustomFormsData(defaultTemplate);
            setContinueButtonText(templateForm.SubmitButtonText);
          }

          setDefaultTemplateId(defaultTemplate.Id);
          setTemplateId(defaultTemplate.Id);
          setShowSummaryCard(defaultTemplate.SummaryCardEnabled);
        }
        setState(9)
      } else {
        await api.addRequestEmail(data);
        setState(2);
      }
    } catch {
      setState(4);
    }
  };

  const setCustomFormsData = (answerTemplate: any)=>{
    setCurrentTemplate(answerTemplate);
    const customFormList =  answerTemplate.TemplateForms.map((templateForm, index)=>{
    const customFormAnswer = customForms[templateForm.Order] ? customForms[templateForm.Order].CardAnswerJson : '{}';
        return {
          TemplateFormId: templateForm.Id,
          FormName: templateForm.Name,
          Order: templateForm.Order,
          CardTemplateJson: templateForm.Form.Definition,
          CardAnswerJson: JSON.parse(customFormAnswer),
          SubmitButtonText: templateForm.SubmitButtonText
        };
      });  
    setCustomForms(customFormList);
    setActiveForm(customFormList[0]);
  }

  const submitFileUpload = async (data: any, ticketCustomForms: any[]) => {
    const cfData = { "messageId": data['responseId'] };
    let updatedFormFiles: any[] = [];
    for(const formFiles of customFormListAttachmentsProps){
      const filesToAttach = await startFileUploadSession(api, uploadHelper, formFiles.Files, 0, true, JSON.stringify(cfData));
      updatedFormFiles.push({
        TemplateFormId: formFiles.TemplateFormId,
        Files: filesToAttach,
        Message: formFiles.Message,
        IsPrivate: formFiles.IsPrivate
      });
    }
    data["fluentFilesUploadData"] = JSON.stringify(updatedFormFiles);
  }

  const submitWithFileAttachments = async(data: any) => {
    let ticketCustomForms = [];
    customForms.filter(x=>x.IsAnswered).forEach(form =>{
      const cardAnswer = JSON.stringify(form.CardAnswerJson);
      const cardAnswerObj = FormatCardAnswerJson(cardAnswer, form.CardTemplateJson);    
      ticketCustomForms.push({TemplateFormId: form.TemplateFormId, FormName: form.FormName, Order: form.Order, CardTemplateJson: form.CardTemplateJson, CardAnswerJson: cardAnswerObj });
    }); 
    data['customForms'] = JSON.stringify(ticketCustomForms);

    const hasAttachments = customForms?.filter(x=>x.IsAnswered)?.some(f => f.CardAnswerJson?.[UploadFile.ElementId]?.length > 0) ?? false; 
    if (hasAttachments)
      await submitFileUpload(data, ticketCustomForms);

    await api.addRequestEmail(data);
  }

  const submitNewRequest = async () => {
    try {

      if (CreateRequest_TriggerExecuteButton()) return;

      setIsSubmitClicked(true);
      const data = JSON.parse(decodeBase64Unicode(searchParams.get('data')));
      data.responseSubject = request;
      data.responseTeam = defaultTeam;
      data.cardResponse = FormatCardAnswerJson(JSON.stringify(templateResponse), answer, false);
      data.cardTemplateBody = RemoveRequest_ActionSet(answer, t);
      data.cardTemplateId = defaultTemplateId;

      await submitWithFileAttachments(data);
      setState(2);
    } catch {
      setState(4);
    }
  };

  const deflectTicket = async (data: any) => {
    try {
      if (data.isDeflected) {
        await api.addRequestEmail(data);
        setState(3);
      } else
        newrequest(data);
    } catch {
      setState(4);
    }
  };

  const ticketApproval = async (data: any) => {
    try {
      const approval = await api.getApprovalApproversTicketLifecyclePhase(data['approvalId']);
      const cdmUser = await api.getPlatformUserByFilter(`Email eq '${data['email']}'`);
      const user = cdmUser.data.value[0] as PlatformUser;
      const approvers = approval.Approvers;
      let currentApprover = approvers.find(i => i.ApproverId == user.Id);

      if (approval.ApprovalState === TicketApprovalState.Approved) {
        setState(6);
        return;
      }
      if (approval.ApprovalState === TicketApprovalState.Rejected) {
        setState(7);
        return;
      }
      if (currentApprover != null && currentApprover.HasApproved != TicketApprovalState.Pending) {
        setState(5);
        return;
      }

      const approvalState = CheckApprovalStateChange(approvers, approval.IsRequiredByAll);
      if (approvalState != 'pending') {
        setState(5);
        return;
      }

      if (!(approval.TicketLifecyclePhase?.IsCurrent ?? true)) {
        setApprovalTitle(approval.Title);
        setState(8);
        return;
      }

      await api.addRequestEmail(data);
      if (data["isApprovalAccepted"]) {
        setState(6);
      } else {
        setState(7);
      }
    } catch {
      setState(4);
    }
  };

  const CheckApprovalStateChange = (approvers: any[], isRequiredByAll: boolean) => {
    if (approvers.some(x => x.HasApproved == TicketApprovalState.Rejected)) 
      return "rejected";

    const isApproved = isRequiredByAll ? approvers.every(x => x.HasApproved == TicketApprovalState.Approved) : approvers.some(x => x.HasApproved == TicketApprovalState.Approved);
    return isApproved ? "approved" : "pending";
  }
  const getTemplate = async (templateId) => {
    try {
      const results = await Promise.all([
        api.getTicketTemplates(`?$filter=Id eq ${templateId}&$expand=TemplateForms($expand=Form)`),
        api.getDataSources(`${FILTERS_STRINGS.FILTERS_URL_PARAMS}`)
      ]);

      setDataSourceList(results[1].data.value ?? []);
      const result = results[0];

      let state = 1;
      if (result && result.data && result.data.value.length > 0) {
        const templateData = result.data.value[0];
        if (templateData) {
          const cardPayload = JSON.parse(templateData.CardResponseJson);
          cardPayload.body.push(CreateRequest_ActionSet(t));
          setAnswer(JSON.stringify(cardPayload));

          if(templateData?.TemplateForms.length > 0){
            const templateForm = templateData.TemplateForms[0];
            const cardPayload = JSON.parse(templateForm.Form.Definition);
          
            if(cardPayload)
              setAnswer(JSON.stringify(cardPayload));

            setCustomFormsData(templateData);
            setContinueButtonText(templateForm.SubmitButtonText);
          }
          setTemplateId(templateData.Id);
          setShowSummaryCard(templateData.SummaryCardEnabled);
          
        }
      } else {
        state = 10;
      }
      setState(state);
    } catch {
      setState(4);
    }
  };
  const submitTemplate = async () => {
    try {
      if (CreateRequest_TriggerExecuteButton()) return;

      setIsSubmitClicked(true);
      const data = JSON.parse(decodeBase64Unicode(searchParams.get('data')));
      data['response'] = FormatCardAnswerJson(JSON.stringify(templateResponse), answer, false);
      data['templateBody'] = RemoveRequest_ActionSet(answer, t);
      data['templateId'] = templateId;

      await submitWithFileAttachments(data);
      setState(2);
    } catch (e) {
      console.error(e);
      setState(4);
    }
  };

  const loadItems = async () => {
    try {
      await ReloadEndUserCFOptions(api, graphAPI, currentState, setAppState);
      const data = JSON.parse(decodeBase64Unicode(searchParams.get('data')));
      setRequest(data.responseSubject);
      getTemplate(data['template']);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }

  const updateActiveFormAnswer = () => {
      const mergedAnswers = Object.assign(activeForm.CardAnswerJson, templateResponse);
      setActiveForm(preState => ({
          ...preState,
          CardAnswerJson: mergedAnswers
        }));
  }
  
  const redirectToNextForm = async() =>{
      const transitionResult = await api.executeFormTransition(templateId, activeForm.TemplateFormId, activeForm,0);
      const nextTemplateForm = transitionResult.data ?? null;
  
      if(nextTemplateForm){
        const nextForm = customForms[nextTemplateForm.Order] ?? null;
        const cardTemplate = nextForm.CardTemplateJson;
        const cardAnswers = nextForm.CardAnswerJson;
        const formPayload = parseCardTemplate(cardTemplate, cardAnswers, currentState, dataSourceList);
       
        setContinueButtonText(nextTemplateForm.SubmitButtonText);
        setActiveForm(nextForm);
        
        if(formPayload)
          setAnswer(formPayload);
      }else{
        showSummaryCard ? setIsSummaryPage(true) :submitTemplate();
      }
  }
  
  const resetForm = () => {
      const nextForm = customForms[0];
      const formPayload = JSON.parse(parseCardTemplate(nextForm.CardTemplateJson, nextForm.CardAnswerJson, currentState, dataSourceList));
      
      setActiveForm(nextForm);
      setIsSummaryPage(false);
      setContinueButtonText(nextForm.SubmitButtonText);
  
      if(formPayload)
        setAnswer(JSON.stringify(formPayload));
  
      setIsResetForm(false);
  };
  
  const onStartOverForm = async()=>{
      setIsResetForm(true);
      setCustomForms(preState => ([]));
      setCustomFormTemplateData(currentTemplate, setCurrentTemplate, setCustomForms, setActiveForm, currentState, dataSourceList);
  }

  const onMoveToPreviousForm = async()=>{
      const answeredFormList = customForms.filter(x=>x.IsAnswered);
      const lastAnsweredIndex = _.findLastIndex(answeredFormList, x=>x.TemplateFormId==activeForm.TemplateFormId);
      const prevFormIndex = (!isSummaryPage) ? lastAnsweredIndex-1: (answeredFormList.length-1);
      const nextForm = answeredFormList[prevFormIndex];
  
      const formPayload = JSON.parse(parseCardTemplate(nextForm.CardTemplateJson, nextForm.CardAnswerJson, currentState, dataSourceList));
      
      setActiveForm(nextForm);
      setIsSummaryPage(false)
      setContinueButtonText(nextForm.SubmitButtonText);
      setTemplateResponse(nextForm.CardAnswerJson);
      setIsCustomFormLoading(true)
  
      if(formPayload)
        setAnswer(JSON.stringify(formPayload));
  
      markFormsAsUnanswerd(answeredFormList, customForms, lastAnsweredIndex, setCustomForms);
  }

  const onMoveToNextForm = async () =>{ 
    const emailFormTemplate = parseIfJSON(activeForm.CardTemplateJson);
    const emailFormAnswers = parseIfJSON(templateResponse);     
      
    const validInputs = validateRequiredInputs(emailFormTemplate, emailFormAnswers, currentState?.customProperties ?? []);
          
    if(validInputs)
    {
      updateActiveFormAnswer();
      setGoToNextForm(true);
      setTemplateResponse({});
    }
  }

  useEffect(()=>{
    if(isResetForm){
       resetForm();
    }
  },[customForms]);
  
  useEffect(()=>{
    if(!isResetForm)
      updateCustomFormListWithAnswers(customForms, activeForm, setCustomForms);
  
    if(goToNextForm){
      const onMoveToNextForm = async() => { await redirectToNextForm(); };
      onMoveToNextForm();
      setGoToNextForm(false);
    }
        
  },[activeForm]);

  useEffect(() => {
    const data = JSON.parse(decodeBase64Unicode(searchParams.get('data')));
    if (data['template']) {
      setLoading(true);
      loadItems();
    } else if (data['approvalId'])
      ticketApproval(data);
    else
      deflectTicket(data);
  }, []);

  const renderAdaptiveCard = () => {
    return (
       <CreateRequestAdaptiveCard isEndUserPage={!(currentState?.userRoles?.isAuthorizedAgent ?? false)} request={request} cardTemplate={answer} setCardTemplate={setAnswer} templateResponse={templateResponse} setTemplateResponse={setTemplateResponse} dataSourceList={dataSourceList} setRenderComplete={setIsRenderComplete} setCustomFormListAttachmentsProps={setCustomFormListAttachmentsProps} activeFormTemplateId={activeForm?.TemplateFormId} isCustomFormLoading={isCustomFormLoading} />
    )
  }

  const renderSummaryAdaptiveCard= () => {
    return(
      <TicketCustomFormSection customForms={customForms.filter(x=>x.IsAnswered)} disabledFormInputs={true} template={currentTemplate} showSummaryInfo={true} isEndUserPage={!(currentState?.userRoles?.isAuthorizedAgent ?? false)}/>
    )
  }

  return (<ProviderConsumer render={globalTheme => (
    <div className={`h-screen`}>
      {state === 0 || isLoading && (<InitialPageLoader />)}
      {((state === 1 || state === 9) && !isLoading)
        ? (<>
          {!isRenderComplete && (<InitialPageLoader />)}
          <Card style={{ width: '100%', overflowY: 'scroll', visibility: isRenderComplete ? 'visible' : 'hidden' }}>
            <>
              {state === 1 && (
                <div className='email-request-card'>
                  <Card.Body>
                    {isSummaryPage ? renderSummaryAdaptiveCard() : renderAdaptiveCard()}
                  </Card.Body>
                  <Card.Footer>
                    <Flex gap='gap.small' className={'mt-4'} hAlign='end'>
                      {(isSummaryPage || (activeForm.Order > 0)) && <Button content={t('common.buttons.back')} onClick={onMoveToPreviousForm} style={{ marginRight: 2 }} />}
                      {isSummaryPage && <Button  content={t('common.buttons.start-over')} onClick={onStartOverForm} style={{ marginRight: 2 }} />}
                      {!isSummaryPage && <Button primary content={continueButtonText} onClick={onMoveToNextForm} style={{ marginRight: 2 }} />}
                      {isSummaryPage && <Button primary content={t('common.buttons.submit')} onClick={submitTemplate} style={{ marginRight: 2 }} /> }
                    </Flex>
                  </Card.Footer>
                </div>
              )}
              {state === 9 && (
                <div className='email-request-card'>
                  {isSummaryPage ? renderSummaryAdaptiveCard() : renderAdaptiveCard()}
                  <Flex gap='gap.small' className={'mt-4'} hAlign='end'>
                    {(isSummaryPage || (activeForm.Order > 0)) && <Button content={t('common.buttons.back')} onClick={onMoveToPreviousForm} style={{ marginRight: 2 }} />}
                    {isSummaryPage && <Button  content={t('common.buttons.start-over')} onClick={onStartOverForm} style={{ marginRight: 2 }} />}
                    {!isSummaryPage && <Button primary content={continueButtonText} onClick={onMoveToNextForm} style={{ marginRight: 2 }} />}
                    {isSummaryPage &&  <Button
                      disabled={request.length == 0 || isSubmitClicked || (emailConfig?.EnableMultiDepartmentPicker && !defaultTeam)}
                      primary
                      type="submit"
                      content={t('requests.dialog.ask')}
                      styles={{ marginRight: 2 }}
                      onClick={() => {
                        submitNewRequest();}}/> }
                  </Flex>
                 
                </div>
              )}
            </>
          </Card>
        </>)
        : (
          <Card style={{ width: '100%', overflowY: 'scroll' }}>
            <>
              {state === 2 && (<div className='text-center'>{t('requests.dialog.received')}  {t('requests.dialog.close-window')}</div>)}
              {state === 3 && (<div className='text-center'>{t('requests.dialog.glad-we-helped')} {t('requests.dialog.close-window')}</div>)}
              {state === 4 && (<div className='text-center'>{t('requests.dialog.oops')}</div>)}
              {state === 5 && (<div className='text-center'>{t('requests.dialog.responded')}</div>)}
              {state === 6 && (<div className='text-center'>{t('requests.dialog.approved')}  {t('requests.dialog.close-window')}</div>)}
              {state === 7 && (<div className='text-center'>{t('requests.dialog.declined')}  {t('requests.dialog.close-window')}</div>)}
              {state === 8 && (<div className='text-center'><strong>{t('requests.dialog.error')}"{approvalTitle}"</strong>. {t('requests.dialog.not-current-phase')}</div>)}
              {state === 10 && (<ScopeErrorMessage />)}  
            </>
          </Card>
        )}
    </div>
  )} />);
};

export default EmailRequest;